// A product's earlier versions, rebuilt from its change log (docs/claude/products.md,
// *A product's change log*). A save logs the whole product as saved and the
// changed fields' old values, a deletion the whole product it removed, so the
// version before any change is read straight off its entry. Older entries hold
// the changed fields only: they are undone one by one from the nearest version
// held whole, or from the product as stored. Pure and DOM-free.

import type { ProductTemplate } from './types';

/** One logged change, as the change log lists it (newest first). */
export interface LoggedChange {
  action: string;
  /** The changed fields' names, comma-separated. */
  changedFields: string;
  /** JSON of the changed fields' values before the change; a deletion's, of
   *  the whole product it removed. */
  before?: string;
  /** JSON of the whole product as saved (it has its `id`); an older entry's, of
   *  the changed fields' values after the change. */
  after?: string;
}

/** What a log written before drawings were recorded holds in place of one. */
const DRAWING_MARKER = /^\[geometry: .* entities\]$/;
/** Fields every version shares, never logged. */
const UNLOGGED = new Set(['builtin', 'id']);

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v);
const isMarker = (key: string | undefined, v: unknown): boolean => key === 'geometry' && typeof v === 'string' && DRAWING_MARKER.test(v);

function parsed(s: string | undefined): Obj | null {
  if (!s) return {};
  try { const o: unknown = JSON.parse(s); return isObj(o) ? o : null; } catch { return null; }
}

/** A logged value that is a whole product. */
function whole(s: string | undefined): Obj | null {
  const o = parsed(s);
  return o && typeof o.id === 'string' ? o : null;
}

/** The fields a change changed: a whole entry's are the ones it names, an older
 *  one's also every field it logged. */
function fieldsOf(c: LoggedChange, before: Obj, after: Obj): string[] {
  const named = c.changedFields.split(',').map((k) => k.trim()).filter(Boolean);
  const all = whole(c.after) || whole(c.before) ? named : [...named, ...Object.keys(before), ...Object.keys(after)];
  return [...new Set(all)].filter((k) => !UNLOGGED.has(k));
}

/** The fields to show for a change, with the values it logged for them. */
export function shownChange(c: LoggedChange): { fields: string[]; before: Obj; after: Obj } {
  const before = parsed(c.before) ?? {}, after = parsed(c.after) ?? {};
  return { fields: c.action === 'deleted' && whole(c.before) ? [] : fieldsOf(c, before, after), before, after };
}

/** Whether a change can be undone from what it logged: an update that holds the
 *  values of every field it changed. */
function undoable(c: LoggedChange): boolean {
  if (c.action !== 'updated') return false;
  const before = parsed(c.before), after = parsed(c.after);
  if (!before || !after) return false;
  const fields = fieldsOf(c, before, after);
  return fields.length > 0 && fields.every((k) => k in before || k in after);
}

/** Where rebuilding the version before change `index` starts: the nearest
 *  version an entry holds whole — a save's, the version after it; a deletion's,
 *  the version before it — else the product as stored. `from` is the first
 *  change to undo from there. */
function startOf(log: readonly LoggedChange[], index: number): { from: number; held: Obj | null } {
  for (let k = index; k >= 0; k--) {
    const c = log[k];
    if (c.action === 'deleted') {
      const b = whole(c.before);
      if (b) return { from: k + 1, held: b };
    } else {
      const a = whole(c.after);
      if (a) return { from: k, held: a };
    }
  }
  return { from: 0, held: null };
}

/** Whether the version before change `index` of a log (newest first) can be
 *  rebuilt: every change from the nearest version held whole down to it undone. */
export function restorable(log: readonly LoggedChange[], index: number): boolean {
  if (!(index >= 0 && index < log.length)) return false;
  const { from } = startOf(log, index);
  for (let i = from; i <= index; i++) if (!undoable(log[i])) return false;
  return true;
}

/** Whether a value matches what a change logged for it (a drawing marker stands
 *  for any drawing). */
function matches(v: unknown, logged: unknown, key?: string): boolean {
  if (isMarker(key, logged)) return isObj(v);
  if (Array.isArray(logged)) return Array.isArray(v) && v.length === logged.length && logged.every((x, i) => matches(v[i], x));
  if (isObj(logged)) {
    if (!isObj(v)) return false;
    const keys = new Set([...Object.keys(logged), ...Object.keys(v)]);
    for (const k of keys) if (!matches(v[k], logged[k], k)) return false;
    return true;
  }
  return v === logged;
}

/** A logged value with any drawing it holds only as a marker taken from the
 *  version it replaces (`now`): a part's by its id, else by its place (an older
 *  editor renamed the ids, `gen` to `p0`). */
function withDrawings(old: unknown, now: unknown, onMarker: () => void, key?: string): unknown {
  if (isMarker(key, old)) { onMarker(); return isObj(now) ? now : undefined; }
  if (Array.isArray(old)) {
    const cur = Array.isArray(now) ? now : [];
    return old.map((x, i) => {
      const id = isObj(x) ? x.id : undefined;
      const match = (id !== undefined ? cur.find((y) => isObj(y) && y.id === id) : undefined) ?? cur[i];
      return withDrawings(x, match, onMarker);
    });
  }
  if (isObj(old)) {
    const out: Obj = {};
    for (const [k, v] of Object.entries(old)) {
      const r = withDrawings(v, isObj(now) ? now[k] : undefined, onMarker, k);
      if (r !== undefined) out[k] = r;
    }
    return out;
  }
  return old;
}

export interface RestoredVersion {
  /** The product as it was before the change. */
  product: ProductTemplate;
  /** A change undone had logged a drawing only as a marker (logs from before
   *  drawings were recorded): the newer version's drawing is kept, if any. */
  keptDrawing: boolean;
  /** Fields that did not hold what a change left in them: a later change went
   *  unlogged, so they may not be as they were. */
  unlogged: string[];
}

/** The product as it was before change `index` of its `log` (newest first): the
 *  nearest version held whole (`startOf`; the stored product, `current`, when
 *  none is) with that change and every one between undone. Null when one of them
 *  cannot be undone (`restorable`). */
export function productBefore(current: ProductTemplate, log: readonly LoggedChange[], index: number): RestoredVersion | null {
  if (!restorable(log, index)) return null;
  const { from, held } = startOf(log, index);
  let keptDrawing = false;
  const kept = (): void => { keptDrawing = true; };
  // An older deletion logged its product's drawings as markers.
  const p = (held ? withDrawings(held, current, kept) : JSON.parse(JSON.stringify(current))) as Obj;
  const unlogged = new Set<string>();
  for (let i = from; i <= index; i++) {
    const before = parsed(log[i].before)!, after = parsed(log[i].after)!;
    for (const k of fieldsOf(log[i], before, after)) {
      if (!matches(p[k], after[k], k)) unlogged.add(k);
      const v = k in before ? withDrawings(before[k], p[k], kept, k) : undefined;
      if (v === undefined) delete p[k];
      else p[k] = v;
    }
  }
  return { product: p as unknown as ProductTemplate, keptDrawing, unlogged: [...unlogged] };
}

/** The product a deletion logged (its `before`), whole; null when it logged no
 *  product. A deletion logged before drawings were recorded holds each drawing as
 *  a marker: the product comes back without them (`lostDrawing`). */
export function deletedVersion(before: string | undefined): { product: ProductTemplate; lostDrawing: boolean } | null {
  const held = whole(before);
  if (!held) return null;
  let lostDrawing = false;
  const product = withDrawings(held, undefined, () => { lostDrawing = true; }) as unknown as ProductTemplate;
  return { product: { ...product, builtin: false }, lostDrawing };
}

/** A logged value for reading: each drawing in it as `label(entities)`. */
export function drawingsSummarized(v: unknown, label: (entities: number) => string, key?: string): unknown {
  if (key === 'geometry' && isObj(v) && Array.isArray(v.entities)) return label(v.entities.length);
  if (Array.isArray(v)) return v.map((x) => drawingsSummarized(x, label));
  if (isObj(v)) return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, drawingsSummarized(x, label, k)]));
  return v;
}
